import {useQueryClient} from '@tanstack/react-query'
import {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
} from 'react'
import {useParams} from 'react-router-dom'
import {useAppConfig} from '../../context/app/AppContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout, getComposedDataList} from '../form/Constant'
import AuditLogButton from '../generic-log/AuditLogButton'
import GenericLogTable from '../generic-log/GenericLogTable'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {permissionOptionKeys} from '../role/RoleConstant'
import {useRoleContext} from '../role/RoleProvider'
import {usePatientContext} from '../styled-component/information/StyleDetailInformationWrapper'
import {parameterList} from './ParameterConstants'
import {
    useParameterByUser,
    useParameterCRUD,
} from './query-keys/ParameterKeysManager'

const ParameterForm = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        // showForm: handleShowForm,
        // hideForm: handleHideForm,
    }))

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {myRolePermissions} = useRoleContext()
    const {handleCheckPermissionByKey} = usePatientContext()
    //#endregion

    //#region Ref
    const genericRef = useRef(null)
    //#endregion

    //#region ID
    const {id: idString} = useParams()
    const patientId = useMemo(() => {
        return !!idString ? parseInt(idString) : null
    }, [idString])
    //#endregion

    // const handleShowForm = useCallback(() => {
    //     blockOverflownScroll()
    // }, [])

    // const handleHideForm = useCallback(() => {
    //     unblockOverflownScroll()
    // }, [])

    //#region Query
    const queryClient = useQueryClient()
    const {data: parameterData} = useParameterByUser(
        user?.id,
        patientId,
        !!user?.id && !!patientId,
    )

    const {mutateAsync: createOrUpdateParameter} = useParameterCRUD(
        !!parameterData?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
        user?.id,
    )

    // const {mutate: handleMutateParameter} = useMutation(...CustomForm.)

    // const {mutate} = useMutation(
    //     data => createOrUpdateGenericInfo('parameter', user?.id, data),
    //     {
    //         onSuccess: data => {
    //             queryClient.setQueryData(
    //                 [QueryKey, user?.id, patientId],
    //                 () => data,
    //             )

    //             showNotification(
    //                 severityNotification.success,
    //                 'Update successfully !',
    //             )
    //             // handleHideForm()
    //         },
    //     },
    // )
    //#endregion

    // useEffect(() => {
    //     if (!!QueryKey && !!patientId) {
    //         detailEvent.on(
    //             'DETAIL-INFORMATION-RELOAD-' + QueryKey + patientId,
    //             refetch,
    //         )

    //         return () => {
    //             removeDetailInformationReload(QueryKey + patientId)
    //         }
    //     }
    // }, [patientId, refetch])

    const handleOnSubmit = useCallback(
        async data => {
            let toPostData = {
                ...data,
                userId: patientId,
            }
            const res = await createOrUpdateParameter(toPostData)

            if (res?.id) {
                const {parameterKeys} = QueryKeyManager(
                    QueryKeyTypes.parameter,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries({
                    queryKey: parameterKeys.pattern(),
                })
            }
        },
        [user?.id, patientId, queryClient, createOrUpdateParameter],
    )

    const parameterDataList = useMemo(() => {
        return getComposedDataList(parameterList, parameterData)
    }, [parameterData])

    const logList = {
        user: user,
        queryTableKey: 'Parameter',
        filterData: {refId: parameterData?.id, type: 'parameterLog'},
        logColumnList: [
            {key: 'heartRateLower'},
            {key: 'heartRateUpper'},
            {key: 'respirationLower'},
            {key: 'respirationUpper'},
            {key: 'temperatureLower'},
            {key: 'temperatureUpper'},
            {key: 'systolicLower'},
            {key: 'systolicUpper'},
            {key: 'oxygenLower'},
            {key: 'oxygenUpper'},
            {key: 'glucoseLower'},
            {key: 'glucoseUpper'},
            {key: 'stepsLower'},
            {key: 'stepsUpper'},
            {key: 'sleepLower'},
            {key: 'sleepUpper'},
            {key: 'comment'},
        ],
    }

    return (
        <div className="col-12 p-0">
            <div className="flex align-items-center flex-wrap col-12 px-3">
                <div
                    className={`col-12 m-0 mb-1 p-0 flex flex-column md:flex-row gap-2 align-items-center justify-content-between`}>
                    <h5 className="m-0 text-700 text-lg">
                        {translateCommons(
                            "The audit log shows various changes in patient's parameters, and you can view the entire change history.",
                        )}
                    </h5>
                    <div className="w-20rem">
                        <AuditLogButton
                            label={translateCommons(
                                "Show Parameter's Audit Log",
                            )}
                            onClick={() => {
                                if (!!patientId && !!parameterData?.id) {
                                    genericRef.current.showForm({
                                        refId: parameterData?.id,
                                        type: 'parameterLog',
                                    })
                                } else {
                                    showNotification(
                                        severityNotification.warn,
                                        'No data found !!',
                                    )
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <hr />
            <CustomForm
                onSubmit={handleOnSubmit}
                hideSubmit={
                    !handleCheckPermissionByKey(
                        permissionOptionKeys.allowToAddParameters,
                    )
                }
                layout={formLayout.grid}
                userData={props.userData}
                dataList={parameterData ? parameterDataList : []}
                logList={logList}
            />
            <GenericLogTable ref={genericRef} />
        </div>
    )
})

export default ParameterForm
